import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import { Tabs } from '../../../components/Tabs';
import Toast from 'react-native-toast-message';
import { platformSettingsService } from '../../../services/central-api/platform-settings';
import { TextInputMask } from 'react-native-masked-text';
import { ScrollView } from 'react-native-web-hover';
import CustomButton from '../../../components/CustomButton';
import { polygonService } from '../../../services/central-api/polygons';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import { useAuth } from '../../../context/Auth';
import EventIntegrationTable from './EventIntegrationTable';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import FrequentlyAskedQuestionsTable from './FrequentlyAskedQuestionsTable';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import EmailRecipients from './EmailRecipients';
import LoginAttemptLimits from './LoginAttemptLimits';
import AccessCredentials from './AccessCredentials';
import { RootNavigation } from '../../../Routes';

export default function PlatformSettings({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();
    const { styles, theme } = useStyles(styleSheet);

    const [sessionValidityTime, setSessionValidityTime] = useState(0);
    const [sessionQuantityLimit, setSessionQuantityLimit] = useState(0);
    const [mobileSessionValidityTime, setMobileSessionValidityTime] = useState(0);
    const [mobileSessionQuantityLimit, setMobileSessionQuantityLimit] = useState(0);
    const [polygonsType, setPolygonsType] = useState<{ type: string; }[]>([]);
    const [occurrencePolygonMap, setOccurrencePolygonMap] = useState<string>('');
    const [thirdPartyPolygonMap, setThirdPartyPolygonMap] = useState<string>('');
    const [occurrenceAreaNameMap, setOccurrenceAreaNameMap] = useState<string>('');

    async function getSessionValidityTime() {
        try {
            const session = await platformSettingsService.getSession();
            setSessionValidityTime(session.sessionValidityTime / 60);
            setSessionQuantityLimit(session.sessionQuantityLimit);
            setMobileSessionValidityTime(session.mobileSessionValidityTime / 60);
            setMobileSessionQuantityLimit(session.mobileSessionQuantityLimit);
        } catch (err) {
            console.error(err);
        }
    }

    async function getMapLimits() {
        try {
            const mapLimits = await platformSettingsService.getMapLimits();
            setOccurrencePolygonMap(mapLimits.occurrenceMapPolygon || '');
            setThirdPartyPolygonMap(mapLimits.thirdPartyCameraMapPolygon || '');
            setOccurrenceAreaNameMap(mapLimits.occurrenceAreaNameMapPolygon || '');
        } catch (err) {
            console.error(err);
        }
    }

    async function saveSessionValidityTime() {
        try {
            await platformSettingsService.updateSession({
                sessionValidityTime: sessionValidityTime * 60,
                sessionQuantityLimit,
                mobileSessionValidityTime: mobileSessionValidityTime * 60,
                mobileSessionQuantityLimit,

            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function saveMapLimits() {
        try {
            await platformSettingsService.updateMapLimits(occurrencePolygonMap, thirdPartyPolygonMap, occurrenceAreaNameMap);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function getPolygonsType() {
        try {
            const polygonsType = await polygonService.getPolygonsType();
            setPolygonsType(polygonsType);
        } catch (err) {
            console.error(err);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getSessionValidityTime();
            getPolygonsType();
            getMapLimits();
        }
    }, [isFocused, navigation]));

    return <Container>
        <Header
            title={translate('platformSettings')}
        />
        <Card>
            <Tabs tabs={[{
                key: 'sessions',
                label: translate('sessions')
            }, {
                key: 'mapLimits',
                label: translate('mapLimits')
            }, {
                key: 'eventsIntegration',
                label: translate('eventsIntegration')
            }, {
                key: 'faq',
                label: 'FAQ'
            }, {
                key: 'emailRecipient',
                label: translate('emailRecipients')
            }, {
                key: 'loginAttemptLimits',
                label: translate('loginControl')
            }, {
                key: 'accessCredentials',
                label: translate('credentialsAccess')
            }]}>
                {({ selectedTab }) => {
                    if (selectedTab === 'sessions') {
                        return <>
                            <ScrollView >
                                <View style={{ flex: 1, gap: 30 }}>
                                    <View style={{ flex: 1, gap: 10 }}>
                                        <MyAppText style={styles.sessionTitle}>{translate('desktop')}</MyAppText>
                                        <MyAppText style={{ color: theme.colors.labelColor, fontSize: 14, fontWeight: 'bold' }}>
                                            {translate('sessionDuration')}
                                        </MyAppText>
                                        <View style={[{ flexDirection: 'row' }]}>
                                            <TextInputMask
                                                value={String(sessionValidityTime)}
                                                includeRawValueInChangeText={true}
                                                onChangeText={(maskedText, rawText) => {
                                                    setSessionValidityTime(Number(rawText));
                                                }}
                                                type={'only-numbers'}
                                                style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                                            />
                                            <MyAppText style={styles.inputAttach}>{translate('minutes')}</MyAppText>
                                        </View>
                                        <MyAppText style={{ color: theme.colors.card.detailText, fontSize: 14 }}>{
                                            translate('sessionDurationExplanation')}
                                        </MyAppText>
                                        <MyAppText style={{ color: theme.colors.labelColor, fontSize: 14, fontWeight: 'bold' }}>
                                            {translate('sessionLimit')}
                                        </MyAppText>
                                        <View style={[{ flexDirection: 'row' }]}>
                                            <TextInputMask
                                                value={String(sessionQuantityLimit)}
                                                includeRawValueInChangeText={true}
                                                onChangeText={(maskedText, rawText) => {
                                                    setSessionQuantityLimit(Number(rawText));
                                                }}
                                                type={'only-numbers'}
                                                style={[styles.input, { maxWidth: 70, textAlign: 'center' }]}
                                            />
                                        </View>
                                        <MyAppText style={{ color: theme.colors.card.detailText, fontSize: 14 }}>{
                                            translate('sessionLimitExplanation')}
                                        </MyAppText>
                                    </View>
                                    <View style={{ flex: 1, gap: 10 }}>
                                        <MyAppText style={styles.sessionTitle}>{translate('mobile')}</MyAppText>
                                        <MyAppText style={{ color: theme.colors.labelColor, fontSize: 14, fontWeight: 'bold' }}>
                                            {translate('sessionDuration')}
                                        </MyAppText>
                                        <View style={[{ flexDirection: 'row' }]}>
                                            <TextInputMask
                                                value={String(mobileSessionValidityTime)}
                                                includeRawValueInChangeText={true}
                                                onChangeText={(maskedText, rawText) => {
                                                    setMobileSessionValidityTime(Number(rawText));
                                                }}
                                                type={'only-numbers'}
                                                style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                                            />
                                            <MyAppText style={styles.inputAttach}>{translate('minutes')}</MyAppText>
                                        </View>
                                        <MyAppText style={{ color: theme.colors.card.detailText, fontSize: 14 }}>{
                                            translate('sessionDurationExplanation')}
                                        </MyAppText>
                                        <MyAppText style={{ color: theme.colors.labelColor, fontSize: 14, fontWeight: 'bold' }}>
                                            {translate('sessionLimit')}
                                        </MyAppText>
                                        <View style={[{ flexDirection: 'row' }]}>
                                            <TextInputMask
                                                value={String(mobileSessionQuantityLimit)}
                                                includeRawValueInChangeText={true}
                                                onChangeText={(maskedText, rawText) => {
                                                    setMobileSessionQuantityLimit(Number(rawText));
                                                }}
                                                type={'only-numbers'}
                                                style={[styles.input, { maxWidth: 70, textAlign: 'center' }]}
                                            />
                                        </View>
                                        <MyAppText style={{ color: theme.colors.card.detailText, fontSize: 14 }}>{
                                            translate('sessionLimitExplanation')}
                                        </MyAppText>
                                    </View>
                                </View>
                            </ScrollView>
                            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                                <CustomButton
                                    onPress={saveSessionValidityTime}
                                    text={translate('save')}
                                />
                            </View>
                        </>;
                    } else if (selectedTab === 'mapLimits') {
                        return <>
                            <View style={{ flex: 1, gap: 30 }}>
                                <FilterDropdown
                                    label={translate('occurrenceMapLimits')}
                                    items={[
                                        { label: '', value: undefined },
                                        ...polygonsType.map((polygon) => {
                                            return {
                                                label: polygon.type,
                                                value: polygon.type
                                            };
                                        })
                                    ]}
                                    multiple={false}
                                    setValue={setOccurrencePolygonMap}
                                    value={occurrencePolygonMap}
                                    zIndex={4}
                                />
                                <FilterDropdown
                                    label={translate('thirdPartyMapLimits')}
                                    items={[
                                        { label: '', value: undefined },
                                        ...polygonsType.map((polygon) => {
                                            return {
                                                label: polygon.type,
                                                value: polygon.type
                                            };
                                        })
                                    ]}
                                    multiple={false}
                                    setValue={setThirdPartyPolygonMap}
                                    value={thirdPartyPolygonMap}
                                    zIndex={3}
                                />
                                <FilterDropdown
                                    label={translate('occurrenceAreaNameMap')}
                                    items={[
                                        { label: '', value: undefined },
                                        ...polygonsType.map((polygon) => {
                                            return {
                                                label: polygon.type,
                                                value: polygon.type
                                            };
                                        })
                                    ]}
                                    multiple={false}
                                    setValue={setOccurrenceAreaNameMap}
                                    value={occurrenceAreaNameMap}
                                    zIndex={2}
                                />
                            </View>
                            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                                <CustomButton
                                    onPress={saveMapLimits}
                                    text={translate('save')}
                                />
                            </View>
                        </>;
                    } else if (selectedTab === 'eventsIntegration') {
                        return <EventIntegrationTable authenticatedUser={authenticatedUser} navigation={navigation} />;
                    } else if (selectedTab === 'faq') {
                        return <FrequentlyAskedQuestionsTable navigation={navigation} />;
                    } else if (selectedTab == 'emailRecipient') {
                        return <EmailRecipients />;
                    } else if (selectedTab == 'loginAttemptLimits') {
                        return <LoginAttemptLimits />;
                    } else if (selectedTab == 'accessCredentials') {
                        return <AccessCredentials />;
                    }
                }}
            </Tabs>
        </Card>
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    sessionTitle: {
        fontSize: 24,
        color: theme.colors.labelColor,
        fontWeight: 'bold',
        fontFamily: 'Open Sans',
    },
    input: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.container.background,
        maxHeight: 40,
        padding: 10,
        color: theme.colors.textColor,
        borderWidth: 1,
        borderColor: theme.colors.container.border,
        borderRadius: 4
    },
    inputAttached: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.container.background,
        maxHeight: 40,
        padding: 10,
        color: theme.colors.textColor,
        borderWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopStartRadius: 4,
        borderBottomStartRadius: 4,
    },
    inputAttach: {
        color: theme.colors.textColor,
        fontSize: 13,
        backgroundColor: theme.colors.container.border,
        maxHeight: 40,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopEndRadius: 4,
        borderBottomEndRadius: 4,
        padding: 11,
    },
}));

